import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import {
  User,
  Mail,
  Phone,
  CreditCard,
  Car,
  Calendar,
  DollarSign,
  FileText,
  CheckCircle,
  Clock,
  AlertCircle,
  Calculator,
} from "lucide-react";
import { formatDate } from "@/common/helpers/date.helpers";
import { formatCurrency, maskPhone } from "@/common/helpers/mask.helpers";
import { useLoanDashboardContext } from "../useLoanDashboardContext";
import { useNavigate } from "react-router-dom";
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import { updateLoanStatus } from "@/common/services/loans.api";
import { LOAN_STATUS } from "@/types/loan.type";

const getStatusConfig = (status: string) => {
  switch (status) {
    case "under_analysis":
      return {
        label: "Em Análise",
        variant: "secondary" as const,
        icon: Clock,
        color: "text-yellow-600",
      };
    case "approved":
      return {
        label: "Aprovado",
        variant: "default" as const,
        icon: CheckCircle,
        color: "text-green-600",
      };
    case "rejected":
      return {
        label: "Rejeitado",
        variant: "destructive" as const,
        icon: AlertCircle,
        color: "text-red-600",
      };
    default:
      return {
        label: "Pendente",
        variant: "outline" as const,
        icon: Clock,
        color: "text-gray-600",
      };
  }
};
export function LoanDetails() {
  const navigate = useNavigate();
  const { loan } = useLoanDashboardContext();
  const statusConfig = getStatusConfig(loan.status);
  const { notifySuccess, notifyError } = useToastContext();
  const StatusIcon = statusConfig.icon;
  const totalSubscriptions = loan.subscriptionInfo.length;
  const totalPayments = loan.subscriptionInfo.reduce(
    (sum, sub) => sum + sub.totalPayments,
    0
  );
  const totalReceived = loan.subscriptionInfo.reduce(
    (sum, sub) => sum + sub.paymentReceived,
    0
  );
  const totalPending = loan.subscriptionInfo.reduce(
    (sum, sub) => sum + sub.paymentPending,
    0
  );
  const progressPercentage =
    totalPayments > 0 ? (totalReceived / totalPayments) * 100 : 0;

  const handleUpdateStatus = async (newStatus: LOAN_STATUS) => {
    if (!newStatus || newStatus === loan.status) return;
    if (!loan._id) {
      console.error("Loan ID is missing");
      return;
    }
    const result = await updateLoanStatus(loan._id, newStatus);
    if (!result.ok) {
      notifyError(result.message as string);
      return;
    }
    notifySuccess("Status do empréstimo atualizado com sucesso");
  };

  return (
    <div className="space-y-6 pb-8">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl text-balance">
                Solicitação de Empréstimo
              </CardTitle>
              <div className="text-sm text-muted-foreground mt-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-amber-800">
                    Este empréstimo é somente para cobranças geradas, e não para
                    futuras ou para quitar a assinatura.
                  </p>
                </div>
              </div>
              <p className="text-muted-foreground mt-1">ID: {loan._id}</p>
            </div>
            <div className="flex items-center gap-2">
              <StatusIcon className={`h-5 w-5 ${statusConfig.color}`} />
              <Badge variant={statusConfig.variant}>{statusConfig.label}</Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Criado em:</span>
              <span className="font-medium">{formatDate(loan.createdAt)}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Atualizado em:</span>
              <span className="font-medium">{formatDate(loan.updatedAt)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Informações do Cliente */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Informações do Cliente
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div>
                <a
                  href={`/clientes/${loan.renterInfo.renterId}`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`/clientes/${loan.renterInfo.renterId}`);
                  }}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-lg text-balance hover:underline hover:text-blue-500"
                >
                  {loan.renterInfo.renterName}
                </a>
                <p className="text-sm text-muted-foreground">
                  ID:
                  <a
                    href={`/clientes/${loan.renterInfo.renterId}`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/clientes/${loan.renterInfo.renterId}`);
                    }}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline hover:text-blue-500"
                  >
                    {loan.renterInfo.renterId}
                  </a>
                </p>
              </div>
            </div>

            <Separator />

            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium">{loan.renterInfo.email}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">WhatsApp</p>
                  <p className="font-medium">
                    {maskPhone(loan.renterInfo.whatsapp)}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <CreditCard className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">CPF</p>
                  <p className="font-medium">{loan.renterInfo.cpf}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Car className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Veículo</p>
                  <p className="font-medium">
                    <a
                      href={`/veiculos/${loan.renterInfo.licensePlate}`}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(`/veiculos/${loan.renterInfo.licensePlate}`);
                      }}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline hover:text-blue-500"
                    >
                      {loan.renterInfo.licensePlate}
                    </a>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    ID:
                    <a
                      href={`/veiculos/${loan.renterInfo.vehicleId}`}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(`/veiculos/${loan.renterInfo.vehicleId}`);
                      }}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline hover:text-blue-500"
                    >
                      {loan.renterInfo.vehicleId}
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Informações de Pagamento */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Informações de Pagamento
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  Valor Pendente
                </span>
                <span className="text-2xl font-bold text-destructive">
                  {formatCurrency(loan.paymentInfo.totalAmountPending)}
                </span>
              </div>
            </div>

            <Separator />

            <div>
              <p className="text-sm text-muted-foreground mb-2">
                Pagamentos Externos (
                {loan.paymentInfo.externalPaymentsIds.length})
              </p>
              <div className="max-h-32 overflow-y-auto space-y-1">
                {loan.paymentInfo.externalPaymentsIds.map(
                  (paymentId, index) => (
                    <div
                      key={paymentId}
                      className="text-xs font-mono bg-muted/30 p-2 rounded"
                    >
                      {index + 1}. {paymentId}
                    </div>
                  )
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ✅ NOVO CARD: Detalhes da Calculadora de Empréstimo */}
      {loan.loanCalculation && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5" />
              Detalhes Calculados do Empréstimo
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Informação sobre a regra aplicada */}
            <div
              className={`p-4 rounded-lg border ${
                loan.loanCalculation.ruleApplied === "high"
                  ? "bg-blue-50 border-blue-200"
                  : "bg-green-50 border-green-200"
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <span
                  className={`w-3 h-3 rounded-full ${
                    loan.loanCalculation.ruleApplied === "high"
                      ? "bg-blue-500"
                      : "bg-green-500"
                  }`}
                ></span>
                <span className="font-semibold">
                  {loan.loanCalculation.ruleApplied === "high"
                    ? "🔵 Regra aplicada: Valor alto (> R$ 1.750,00)"
                    : "🟢 Regra aplicada: Valor padrão (≤ R$ 1.750,00)"}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                Parcela semanal de{" "}
                <strong>
                  {formatCurrency(loan.loanCalculation.paymentAmount)}
                </strong>{" "}
                aplicada baseada no valor principal de{" "}
                <strong>
                  {formatCurrency(loan.loanCalculation.principalValue)}
                </strong>
              </p>
            </div>

            {/* Grid com informações principais */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-card p-4 rounded-lg border text-center">
                <p className="text-2xl font-bold text-primary">
                  {formatCurrency(loan.loanCalculation.principalValue)}
                </p>
                <p className="text-sm text-muted-foreground">
                  Valor Emprestado
                </p>
              </div>
              <div className="bg-card p-4 rounded-lg border text-center">
                <p className="text-2xl font-bold text-green-600">
                  {formatCurrency(loan.loanCalculation.paymentAmount)}
                </p>
                <p className="text-sm text-muted-foreground">Parcela Semanal</p>
              </div>
              <div className="bg-card p-4 rounded-lg border text-center">
                <p className="text-2xl font-bold text-blue-600">
                  {loan.loanCalculation.numberOfWeeksRounded}
                </p>
                <p className="text-sm text-muted-foreground">
                  Semanas para Quitar
                </p>
              </div>
            </div>

            <Separator />

            {/* Detalhes financeiros */}
            <div>
              <h4 className="font-semibold mb-3">Resumo Financeiro</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-muted/50 rounded">
                    <span className="text-sm text-muted-foreground">
                      Total a Pagar
                    </span>
                    <span className="font-semibold text-lg">
                      {formatCurrency(loan.loanCalculation.totalAmount)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-muted/50 rounded">
                    <span className="text-sm text-muted-foreground">
                      Juros Totais
                    </span>
                    <span className="font-semibold text-lg text-orange-600">
                      {formatCurrency(loan.loanCalculation.totalInterest)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-muted/50 rounded">
                    <span className="text-sm text-muted-foreground">
                      Multiplicador
                    </span>
                    <span className="font-semibold text-lg">
                      {loan.loanCalculation.totalToPrincipalRatio.toFixed(3)}x
                    </span>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-muted/50 rounded">
                    <span className="text-sm text-muted-foreground">
                      Taxa Semanal
                    </span>
                    <span className="font-semibold text-lg">
                      {(loan.loanCalculation.weeklyRate * 100).toFixed(3)}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-muted/50 rounded">
                    <span className="text-sm text-muted-foreground">
                      Taxa Mensal Equiv.
                    </span>
                    <span className="font-semibold text-lg">
                      {(
                        loan.loanCalculation.monthlyEquivalentRate * 100
                      ).toFixed(3)}
                      %
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-muted/50 rounded">
                    <span className="text-sm text-muted-foreground">
                      Taxa Anual Equiv.
                    </span>
                    <span className="font-semibold text-lg">
                      {(
                        loan.loanCalculation.annualEquivalentRate * 100
                      ).toFixed(2)}
                      %
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Cronograma resumido */}
            <div>
              <h4 className="font-semibold mb-3">Cronograma de Pagamento</h4>
              <div className="bg-muted/30 p-4 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Prazo Exato:</p>
                    <p className="font-semibold">
                      {loan.loanCalculation.numberOfWeeks.toFixed(2)} semanas
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Prazo Comercial:</p>
                    <p className="font-semibold">
                      {loan.loanCalculation.numberOfWeeksRounded} semanas
                      <span className="text-muted-foreground">
                        (≈{" "}
                        {(
                          loan.loanCalculation.numberOfWeeksRounded / 4.33
                        ).toFixed(1)}{" "}
                        meses)
                      </span>
                    </p>
                  </div>
                </div>
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                  <p className="text-sm text-blue-800">
                    <strong>Explicação:</strong> O cliente pagará{" "}
                    {loan.loanCalculation.numberOfWeeksRounded} parcelas
                    semanais de{" "}
                    {formatCurrency(loan.loanCalculation.paymentAmount)},
                    totalizando{" "}
                    {formatCurrency(loan.loanCalculation.totalAmount)}. Os juros
                    de {formatCurrency(loan.loanCalculation.totalInterest)}{" "}
                    representam{" "}
                    {(
                      (loan.loanCalculation.totalInterest /
                        loan.loanCalculation.principalValue) *
                      100
                    ).toFixed(1)}
                    % do valor emprestado.
                  </p>
                </div>
              </div>
            </div>

            {/* Metadados */}
            {loan.loanCalculation.calculatedAt && (
              <div className="text-xs text-muted-foreground border-t pt-4">
                <p>
                  Cálculo realizado em:{" "}
                  {formatDate(loan.loanCalculation.calculatedAt)}
                  {loan.loanCalculation.isCalculationValid && (
                    <span className="ml-2 text-green-600">✓ Validado</span>
                  )}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Informações das Assinaturas */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Assinaturas ({totalSubscriptions})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Resumo Geral */}
          <div className="bg-card p-4 rounded-lg border">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-primary">
                  {totalPayments}
                </p>
                <p className="text-sm text-muted-foreground">
                  Total de Pagamentos
                </p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">
                  {totalReceived}
                </p>
                <p className="text-sm text-muted-foreground">
                  Pagamentos Recebidos
                </p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-yellow-600">
                  {totalPending}
                </p>
                <p className="text-sm text-muted-foreground">
                  Pagamentos Pendentes
                </p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-gray">
                  {progressPercentage.toFixed(1)}%
                </p>
                <p className="text-sm text-muted-foreground">Progresso</p>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Progresso dos Pagamentos</span>
                <span>
                  {totalReceived}/{totalPayments}
                </span>
              </div>
              <Progress value={progressPercentage} className="h-2" />
            </div>
          </div>

          {/* Detalhes das Assinaturas */}
          <div className="space-y-4">
            <h4 className="font-semibold">Detalhes por Assinatura</h4>
            {loan.subscriptionInfo.map((subscription, index) => {
              const subProgress =
                (subscription.paymentReceived / subscription.totalPayments) *
                100;

              return (
                <div
                  key={subscription.subscriptionId}
                  className="border rounded-lg p-4"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h5 className="font-medium">Assinatura {index + 1}</h5>
                    <Badge variant="outline" className="font-mono text-xs">
                      {subscription.subscriptionId}
                    </Badge>
                  </div>

                  <div className="grid grid-cols-3 gap-4 mb-3 text-sm">
                    <div>
                      <p className="text-muted-foreground">Total</p>
                      <p className="font-semibold">
                        {subscription.totalPayments}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Recebidos</p>
                      <p className="font-semibold text-green-600">
                        {subscription.paymentReceived}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Pendentes</p>
                      <p className="font-semibold text-yellow-600">
                        {subscription.paymentPending}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <div className="flex justify-between text-xs">
                      <span>Progresso</span>
                      <span>{subProgress.toFixed(1)}%</span>
                    </div>
                    <Progress value={subProgress} className="h-1.5" />
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Ações */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-3 justify-evenly">
            <Button
              variant="destructive"
              className="flex items-center gap-2"
              onClick={() => handleUpdateStatus(LOAN_STATUS.CANCELED)}
            >
              <AlertCircle className="h-4 w-4" />
              Rejeitar Solicitação
            </Button>
            <Button
              variant="outline"
              className="flex items-center gap-2 bg-transparent"
            >
              <FileText className="h-4 w-4" />
              Gerar Relatório
            </Button>
            <Button
              variant="outline"
              className="flex items-center gap-2 bg-transparent"
            >
              <Mail className="h-4 w-4" />
              Enviar Notificação
            </Button>
            <Button
              className="flex items-center gap-2 bg-green-500"
              onClick={() => handleUpdateStatus(LOAN_STATUS.APPROVED)}
            >
              <CheckCircle className="h-4 w-4" />
              Aprovar Solicitação
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
